import axios from 'axios';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { API_URL } from '../config/api';
import { useAuth } from './AuthContext';

const ModuleContext = createContext();

export const useModules = () => {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModules must be used within a ModuleProvider');
  }
  return context;
};

export const ModuleProvider = ({ children }) => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user && user.tenantId) {
      setLoading(true);
      fetchModules();
    } else if (user && !user.tenantId) {
      // User logged in but no tenantId (superadmin or legacy admin)
      // For legacy admin users, show all modules as enabled
      if (user.role === 'admin' || user.role === 'superadmin') {
        // Fetch all module definitions and mark all as enabled
        fetchAllModulesAsEnabled();
      } else {
        setModules([]);
        setLoading(false);
      }
    } else {
      // No user yet, try to load from localStorage
      const stored = localStorage.getItem('tenantModules');
      if (stored) {
        try {
          setModules(JSON.parse(stored));
        } catch (e) {
          setModules([]);
        }
      }
      setLoading(false);
    }
  }, [user]);

  // Refresh modules when window gains focus (user switches back to tab)
  useEffect(() => {
    const handleFocus = () => {
      if (user?.tenantId) {
        fetchModules(false); // Don't show loading spinner on focus refresh
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user]);

  const fetchAllModulesAsEnabled = async () => {
    setLoading(true);
    try {
      // Fetch all module definitions from backend
      const response = await axios.get(`${API_URL}/api/modules/definitions`);
      const allModules = response.data.modules || [];
      
      // Mark all modules as enabled for legacy admin users
      const enabledModules = allModules.map(module => ({
        ...module,
        enabled: true
      }));
      
      setModules(enabledModules);
      localStorage.setItem('tenantModules', JSON.stringify(enabledModules));
    } catch (error) {
      console.error('Error fetching all modules:', error);
      // Default to showing all modules enabled if fetch fails
      const defaultModules = [
        { code: 'PRODUCTS', name: 'Products', enabled: true, category: 'ecommerce' },
        { code: 'CATEGORIES', name: 'Categories', enabled: true, category: 'ecommerce' },
        { code: 'ORDERS', name: 'Orders', enabled: true, category: 'ecommerce' },
        { code: 'BANNERS', name: 'Banners', enabled: true, category: 'content' },
        { code: 'BLOGS', name: 'Blog', enabled: true, category: 'content' },
        { code: 'WORKSHOPS', name: 'Workshops', enabled: true, category: 'content' },
        { code: 'SEO', name: 'SEO Tools', enabled: true, category: 'marketing' },
        { code: 'REVIEWS', name: 'Reviews', enabled: true, category: 'marketing' },
        { code: 'AUTOMATION', name: 'Automation', enabled: true, category: 'automation' },
        { code: 'AI_CHATBOT', name: 'AI Chatbot', enabled: true, category: 'automation' },
        { code: 'WORKFLOWS', name: 'Workflows', enabled: true, category: 'automation' }
      ];
      setModules(defaultModules);
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async (showLoading = true) => {
    // Only fetch if user has tenantId (tenant_admin or admin with tenant)
    if (!user?.tenantId) {
      if (showLoading) setLoading(false);
      // If no tenantId, set empty modules array (user might be superadmin or regular admin)
      setModules([]);
      return;
    }

    if (showLoading) setLoading(true);

    try {
      // Fetch modules for the authenticated tenant admin using tenantId
      const response = await axios.get(`${API_URL}/api/modules/tenant/${user.tenantId}`);
      const modulesList = response.data.modules || [];
      setModules(modulesList);
      
      // Store in localStorage for quick access
      localStorage.setItem('tenantModules', JSON.stringify(modulesList));
    } catch (error) {
      console.error('Error fetching modules:', error);
      console.error('Error details:', error.response?.data);
      // Try to load from localStorage as fallback
      const stored = localStorage.getItem('tenantModules');
      if (stored) {
        try {
          setModules(JSON.parse(stored));
        } catch (e) {
          setModules([]);
        }
      } else {
        setModules([]);
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const isModuleEnabled = (moduleCode) => {
    const module = modules.find(m => m.code === moduleCode);
    return module?.enabled || false;
  };

  const getEnabledModules = () => {
    return modules.filter(m => m.enabled);
  };

  // Memoize refreshModules to prevent unnecessary re-renders
  // Use useCallback with user?.tenantId to prevent recreation on every render
  const refreshModules = useCallback((showLoading = true) => {
    fetchModules(showLoading);
  }, [user?.tenantId]); // Only recreate if tenantId changes, not entire user object

  const value = {
    modules,
    loading,
    isModuleEnabled,
    getEnabledModules,
    refreshModules
  };

  return (
    <ModuleContext.Provider value={value}>
      {children}
    </ModuleContext.Provider>
  );
};

